import { defineStore } from "pinia";
import { useAuthStore } from "./useAuthStore";
import { useSchemaStore } from "./useSchemaStore";
import _ from "lodash";

const CRUD_URL = import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT;

export const useCrudStore = defineStore("crudStore", {
  state: () => ({}),

  actions: {
    async authenticatedRequest(endpoint, requestBody) {
      const authStore = useAuthStore();
      const current_user = authStore.current_user;

      // Guard: Don't attempt requests if session is already invalid
      if (authStore.invalid_session) {
        console.warn(`Request to ${endpoint} blocked - session already invalidated`);
        return { error: "Session already invalidated", status: 401 };
      }

      // Guard: Ensure user is authenticated
      if (!current_user) {
        console.warn(`Request to ${endpoint} blocked - user not authenticated`);
        return { error: "User is not authenticated", status: 401 };
      }

      try {
        const response = await fetch(`${CRUD_URL}${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            user_id: current_user.id,
            session_id: current_user.session_id,
          },
          body: JSON.stringify(requestBody),
        });

        const result = await response.json();

        // Centralized 401 handling
        if (result.status === 401) {
          authStore.invalid_session = true;
          return { error: "Session expired", status: 401 };
        }

        if (!response.ok) {
          throw new Error(`Network error for ${endpoint}: ${response.statusText}`);
        }

        return result;
      } catch (err) {
        console.error(`Error in ${endpoint}:`, err);
        throw err;
      }
    },
    async find(
      table_name,
      filters = [],
      projection = null,
      sort = null,
      range_start = null,
      range_end = null,
      limit = null,
      sort_ascending = true
    ) {
      if (!table_name) {
        throw new Error("Table name is required");
      }

      const schemaStore = useSchemaStore();

      let data = await this.authenticatedRequest("/find", {
        table_name,
        filters,
        ...(projection && { projection }),
        ...(sort && { sort }),
        ...(range_start !== null && { range_start }),
        ...(range_end !== null && { range_end }),
        ...(limit !== null && { limit }),
        sort_ascending,
      });

      let formatted_data = schemaStore.formatRecords(data, table_name, "inbound");

      return formatted_data;
    },
    async findOne(table_name, filters = [], projection = null) {
      if (!table_name) {
        throw new Error("Table name is required");
      }
      const schemaStore = useSchemaStore();

      const result = await this.authenticatedRequest("/find-one", {
        table_name,
        filters,
        ...(projection && { projection }),
      });

      if (result.data) {
        let formatted_data = schemaStore.formatRecords([result.data], table_name, "inbound");
        return formatted_data[0];
      }

      return null;
    },
    async insert(table_name, data_to_insert) {
      if (!table_name) {
        throw new Error("Table name is required");
      }

      if (!data_to_insert) {
        throw new Error("No data_to_insert found for insert operation");
      }

      const schemaStore = useSchemaStore();

      let cloned_data = _.cloneDeep(data_to_insert);
      let formatted_data = schemaStore.formatRecords(cloned_data, table_name, "outbound");

      return await this.authenticatedRequest("/insert", {
        table_name,
        data_to_insert: formatted_data,
      });
    },
    async update(table_name, filters = [], data_to_update = null, on_conflict = false) {
      // console.log("Running Update");
      if (!table_name) {
        throw new Error("Table name is required");
      }

      if (!data_to_update) {
        throw new Error("No update object found for update operation");
      }

      const schemaStore = useSchemaStore();

      let cloned_data = _.cloneDeep(data_to_update);
      let formatted = schemaStore.formatRecords([cloned_data], table_name, "outbound");

      let formatted_data = formatted[0];

      return this.authenticatedRequest("/update", {
        table_name,
        filters,
        data_to_update: formatted_data,
        on_conflict,
      });
    },
    async delete(table_name, filters = []) {
      if (!table_name) {
        throw new Error("Table name is required");
      }

      if (!filters || filters.length === 0) {
        throw new Error("Filter is required for delete operations");
      }

      return await this.authenticatedRequest("/delete", {
        table_name,
        filters,
      });
    },
  },
});
